export const pad = (value: number) => String(value).padStart(2, '0');

export function uid(prefix = ''): string {
  return `${prefix}${crypto.randomUUID()}`;
}

export const nowIso = () => new Date().toISOString();

/** Local calendar date as YYYY-MM-DD (not UTC, which is a day behind in Queensland mornings). */
export function dateKey(date: Date = new Date()): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Parses YYYY-MM-DD at local noon, which avoids daylight-saving and timezone edge cases. */
export const atNoon = (value: string) => new Date(`${value}T12:00:00`);

export function todayAtNoon(): Date {
  const value = new Date();
  value.setHours(12, 0, 0, 0);
  return value;
}

export function startOfToday(): Date {
  const value = new Date();
  value.setHours(0, 0, 0, 0);
  return value;
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export const minutesOf = (time = '09:00') => {
  const [hours, minutes] = time.includes(':') ? time.split(':').map(Number) : [9, 0];
  return hours * 60 + minutes;
};

export function timeLabel(time = '09:00'): string {
  const [hours, minutes] = time.split(':').map(Number);
  return `${((hours + 11) % 12) + 1}:${pad(minutes || 0)} ${hours >= 12 ? 'PM' : 'AM'}`;
}

export const hourLabel = (hour: number) => timeLabel(`${pad(hour)}:00`);

export const money = (value: number) => `$${Number(value || 0).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;

export const plural = (count: number, word: string, suffix = 's') => `${count} ${word}${count === 1 ? '' : suffix}`;

export function longDate(value: string | Date): string {
  const date = typeof value === 'string' ? atNoon(value) : value;
  return date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });
}

export function shortDate(value?: string): string {
  if (!value) return 'Date not recorded';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return atNoon(value).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Renders an ISO timestamp the way the prototype did (en-AU locale), passing through older free-text values. */
export function dateTime(value?: string): string {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString('en-AU');
}

export function displayDate(value?: string): string {
  if (!value) return '';
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(value) ? atNoon(value) : new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString('en-AU');
}

export const initials = (name: string) => String(name || '').split(/\s+/).filter(Boolean).map((word) => word[0]).join('').slice(0, 2).toUpperCase() || '??';

/** Parses legacy en-AU "d/m/yyyy" strings and ISO values into YYYY-MM-DD. */
export function toDateKey(value?: string): string {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (match) return `${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : dateKey(parsed);
}

/** Converts legacy en-AU "d/m/yyyy, h:mm:ss am" strings into ISO so they sort and format consistently. */
export function toIso(value?: string | number): string {
  if (value === undefined || value === null || value === '') return nowIso();
  if (typeof value === 'number') return new Date(value).toISOString();
  if (/^\d{4}-\d{2}-\d{2}T/.test(value)) return value;
  const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([ap]m)?)?/i);
  if (match) {
    let hours = Number(match[4] || 0);
    if (match[7]?.toLowerCase() === 'pm' && hours < 12) hours += 12;
    if (match[7]?.toLowerCase() === 'am' && hours === 12) hours = 0;
    return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]), hours, Number(match[5] || 0), Number(match[6] || 0)).toISOString();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? nowIso() : parsed.toISOString();
}

export function daysUntil(date: Date): number {
  return Math.ceil((date.getTime() - startOfToday().getTime()) / 86400000);
}

/** For the few places that build HTML strings (Leaflet popups, exported documents). */
export const escapeHtml = (value: unknown) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]!));
